import { useCallback, useMemo } from "react";
import type { EntityItemConfig, Omittable } from "@salestrak/ui";
import {
  useTeamItemsWith,
  useTeamPool,
  type UseTeamPoolOptions,
} from "#app/components/entity-selectors/hooks/use-team-pool";
import {
  usePinnedSelection,
  useValueIds,
} from "#app/components/entity-selectors/use-member-selection";

/**
 * THE team-picker data source — the team pool plus the selections for
 * `value`. The pool loads one page and searches the server, so a selected
 * team past it is fetched by id and pinned above the list, the way
 * `useMemberSelection` pins a member.
 */
export function useTeamSelection({
  value,
  exclude,
  teamAction,
}: Omittable<{ value?: string | string[] | null }> & UseTeamPoolOptions = {}) {
  const { pool: teamPool, loading } = useTeamPool({ exclude, teamAction });
  const ids = useValueIds(value);
  const resolved = useTeamItemsWith(teamPool.items, ids);
  const byId = useMemo(
    () => new Map(resolved.map((item) => [item.id, item])),
    [resolved],
  );
  const itemFor = useCallback(
    (id: string): EntityItemConfig | undefined => byId.get(id),
    [byId],
  );
  const { pool, selections, toIds } = usePinnedSelection(teamPool, ids, itemFor);
  return { pool, selections, toIds, loading };
}
